
import Vue from 'vue'
import axios from 'axios'
import { EventBus } from '@/plugins/eventBus'
import { trackUserProgress } from '@/utils/userProgress'
import { PROGRESSION_ACTIONS } from '@/constants/progressionActions'
import { state } from '@/plugins/state'
import { getUserId } from '@/plugins/token'
import SelectSpace from '@/components/SelectSpace.vue'
import DateTimePicker2 from '@/components/DateTimePicker2.vue'
import AddressPositionMap from '@/components/AddressPositionMap.vue'
import {
  CalendarEvent,
  CalendarEventFormData,
  EventCategory,
  getCategoryColors,
} from '@/types/Calendar'
import {
  filterContactsWithNumericId,
  getCompanyIdsFromSelectedContacts,
  mergeCompaniesById,
  mergeContactsBySelectedIds,
  normalizeContactRecord,
  parseCompaniesListResponse,
  parseContactsListResponse,
  shouldSyncCompanyIds,
  getAddedAndRemovedIds,
  filterContactIdsAfterCompanyRemoval,
  mergeContactIds,
  buildNextCompanyIds,
  shouldSyncContactsOnCompanyChange,
} from '@/utils/calendarEventContacts'
// eslint-disable-next-line @typescript-eslint/no-var-requires
const debounce = require('lodash/debounce')

function getUserDisplayName(isCurrentUser: boolean, fullName: string, meLabel: string): string {
  if (!isCurrentUser) return fullName
  if (fullName && fullName.trim() !== meLabel) return `${meLabel} (${fullName})`
  return meLabel
}

interface EventTypeItem {
  text: string
  value: string
  category: EventCategory
  categoryLabel: string
  color: string
}

type CalendarApiQueryParams = Record<string, string | boolean | number>
type CalendarApiIdQueryParams = Record<string, string | number>

export default Vue.extend({
  name: 'EventFormDialog',
  components: {
    SelectSpace,
    DateTimePicker2,
    AddressPositionMap,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    event: {
      type: Object as () => CalendarEvent | null,
      default: null,
    },
    users: {
      type: Array,
      default: () => [],
    },
    preselectedWorker: {
      type: Object,
      default: null,
    },
    preselectedCompany: {
      type: Object,
      default: null,
    },
    preselectedType: {
      type: String,
      default: '',
    },
    preselectedTitle: {
      type: String,
      default: '',
    },
    preselectedCompanyIds: {
      type: Array as () => number[],
      default: () => [],
    },
    preselectedWorkerIds: {
      type: Array as () => number[],
      default: () => [],
    },
    preselectedUserIds: {
      type: Array as () => number[],
      default: () => [],
    },
    preselectedStartDate: {
      type: String,
      default: '',
    },
    preselectedStartTime: {
      type: String,
      default: '',
    },
    preselectedEndDate: {
      type: String,
      default: '',
    },
    preselectedEndTime: {
      type: String,
      default: '',
    },
    preselectedDescription: {
      type: String,
      default: '',
    },
    preselectedAddress: {
      type: String,
      default: '',
    },
    preselectedResult: {
      type: String,
      default: '',
    },
    preselectedIsPrivate: {
      type: Boolean,
      default: false,
    },
    preselectedContactIds: {
      type: Array as () => number[],
      default: () => [],
    },
    preselectedDealId: {
      type: Number,
      default: null,
    },
    lockCategoryCommercial: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const form: CalendarEventFormData = {
      title: '',
      type: '',
      description: '',
      address: '',
      result: '',
      is_private: false,
      user_ids: [] as number[],
      worker_ids: [] as number[],
      company_ids: [] as number[],
      contact_ids: [] as number[],
      deal_id: null as number | null,
      notification_minutes_before: null as number | null,
      address_position: null,
    }
    return {
      valid: true,
      formSubmitted: false,
      saving: false,
      deleting: false,
      deleteDialog: false,
      showAddressMap: false,
      addressMapDialogKey: 0,
      startDate: '',
      startTime: '09:00',
      endDate: '',
      endTime: '09:30',
      form,
      localUsers: [] as Array<{ id: number; firstname: string; lastname: string; full_name: string }>,
      workers: [] as any[],
      companies: [] as any[],
      contacts: [] as any[],
      deals: [] as Array<{ id: number; title?: string; company_name?: string | null }>,
      loadingWorkers: false,
      loadingCompanies: false,
      loadingContacts: false,
      loadingDeals: false,
      loadingUsers: false,
      loadingResults: false,
      workerSearch: '',
      companySearch: '',
      contactSearch: '',
      dealSearch: '',
      syncingCompanyContact: false,
      resultSuggestions: [] as string[],
      eventTypesConfig: {} as Record<string, Record<string, { user_required: boolean; company_required: boolean; worker_required: boolean }>>,
      categoryColorEdit: '' as string,
      categoryColors: getCategoryColors() as Record<EventCategory, string>,
      titleManuallyEdited: false,
      addressManuallyEdited: false,
      addressSuggestions: [] as Array<{ label: string; city?: string; postcode?: string; coordinates?: [number, number] }>,
      addressSearch: '',
      loadingAddresses: false,
      selectedAddress: null as { label: string; city?: string; postcode?: string; coordinates?: [number, number] } | null,
      selectedAgencyId: state.agency ? Number(state.agency) : null,
    }
  },
  computed: {
    isEditing(): boolean {
      return !!(this.event && this.event.id)
    },
    eventTypeItems(): EventTypeItem[] {
      const colors = this.categoryColors
      const items: EventTypeItem[] = []

      // Use API data (eventTypesConfig is always populated from API)
      if (Object.keys(this.eventTypesConfig).length === 0) {
        return items
      }

      Object.entries(this.eventTypesConfig).forEach(([category, types]) => {
        if (this.lockCategoryCommercial && category !== 'commercial') {
          return
        }
        Object.keys(types).forEach((type) => {
          items.push({
            text: this.$vuetify.lang.t(`$vuetify.event_type.${type}`) as string,
            value: type,
            category: category as EventCategory,
            categoryLabel: this.$vuetify.lang.t(`$vuetify.event_category.${category}`) as string,
            color: colors[category as EventCategory],
          })
        })
      })

      return items
    },
    dealFieldLocked(): boolean {
      return this.lockCategoryCommercial && !!this.preselectedDealId
    },
    dealItems(): Array<{ id: number; title?: string; company_name?: string | null }> {
      return this.deals
    },
    notificationOptions() {
      return [
        { text: this.$vuetify.lang.t('$vuetify.no_notification'), value: null },
        { text: '15 ' + this.$vuetify.lang.t('$vuetify.minutes_before'), value: 15 },
        { text: '30 ' + this.$vuetify.lang.t('$vuetify.minutes_before'), value: 30 },
        { text: '1 ' + this.$vuetify.lang.t('$vuetify.hour_before'), value: 60 },
        { text: '2 ' + this.$vuetify.lang.t('$vuetify.hours_before'), value: 120 },
        { text: '1 ' + this.$vuetify.lang.t('$vuetify.day_before'), value: 1440 },
      ]
    },
    selectedTypeConfig(): { user_required: boolean; company_required: boolean; worker_required: boolean } | null {
      if (!this.form.type) return null
      // Find the type in the config
      for (const category of Object.values(this.eventTypesConfig)) {
        if (category[this.form.type]) {
          return category[this.form.type]
        }
      }
      return null
    },
    userRequired(): boolean {
      return this.selectedTypeConfig?.user_required ?? false
    },
    workerRequired(): boolean {
      return this.selectedTypeConfig?.worker_required ?? false
    },
    companyRequired(): boolean {
      return this.selectedTypeConfig?.company_required ?? false
    },
    selectedUsersInListCount(): number {
      const userIds = new Set(this.availableUsers.map((u: { id: number }) => u.id))
      return (this.form.user_ids || []).filter((id: number) => userIds.has(id)).length
    },
    allUsersSelected(): boolean {
      return this.availableUsers.length > 0 &&
        this.selectedUsersInListCount === this.availableUsers.length
    },
    usersSelectionIndeterminate(): boolean {
      const count = this.selectedUsersInListCount
      return count > 0 && count < this.availableUsers.length
    },
    availableUsers(): Array<{ id: number; firstname: string; lastname: string; full_name: string; email?: string }> {
      // Use localUsers if loaded, otherwise fallback to prop users
      if (this.localUsers.length > 0) {
        return this.localUsers
      }
      // Convert prop users to the expected format
      return (this.users as any[]).map((user: any) => ({
        id: user.id,
        firstname: user.firstname || user.first_name || '',
        lastname: user.lastname || user.last_name || '',
        full_name: user.full_name || `${user.firstname || user.first_name || ''} ${user.lastname || user.last_name || ''}`.trim(),
        email: user.email || '',
      }))
    },
    selectedCategory(): EventCategory | null {
      if (!this.form.type) return null
      // Find the category for the selected type from API config
      for (const [category, types] of Object.entries(this.eventTypesConfig)) {
        if (this.form.type in types) {
          return category as EventCategory
        }
      }
      return null
    },
    eventCreator(): string | null {
      if (!this.isEditing || !this.event) {
        return null
      }
      // Use user_first_name and user_last_name from event (API returns these)
      if (this.event.user_first_name || this.event.user_last_name) {
        return `${this.event.user_first_name || ''} ${this.event.user_last_name || ''}`.trim() || null
      }
      // Fallback: try to find creator in event.users
      if (this.event.users && this.event.users.length > 0 && this.event.user_id) {
        const creatorFromEvent = this.event.users.find((u: any) => u.id === this.event!.user_id)
        if (creatorFromEvent) {
          return `${creatorFromEvent.first_name || ''} ${creatorFromEvent.last_name || ''}`.trim()
        }
      }
      // Fallback: search in availableUsers
      if (this.event.user_id) {
        const creator = this.availableUsers.find((u) => u.id === this.event!.user_id)
        if (creator && creator.full_name) {
          return creator.full_name
        }
      }
      return null
    },
    formattedCreatedDate(): string {
      if (!this.isEditing || !this.event || !this.event.created_at) {
        return ''
      }
      const date = new Date(this.event.created_at)
      const locale = this.$vuetify.lang.current === 'fr' ? 'fr-FR' : 'en-US'
      return date.toLocaleString(locale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    isCurrentUserCreator(): boolean {
      if (!this.isEditing || !this.event) {
        return true // Allow editing when creating new event
      }
      const currentUserId = getUserId()
      if (!currentUserId || currentUserId === 0) {
        return true // If we can't get user ID, allow editing (backend will block if needed)
      }
      const uid = Number(currentUserId)
      if (this.event.user_id && Number(this.event.user_id) === uid) return true
      if (this.event.users && Array.isArray(this.event.users)) {
        return this.event.users.some((u: { id: number }) => Number(u.id) === uid)
      }
      const users = this.event.users as unknown[] | undefined
      if (!this.event.user_id && !users) {
        return true // No creator/responsibles data, allow (backend will block if needed)
      }
      return false
    },
    modalTitle(): string {
      if (!this.isEditing) {
        return this.$vuetify.lang.t('$vuetify.create_event') as string
      }
      if (!this.isCurrentUserCreator) {
        return this.$vuetify.lang.t('$vuetify.view_event') as string
      }
      return this.$vuetify.lang.t('$vuetify.edit_event') as string
    },
    startDateTimeItem(): { dateObject: Date; dateString: string; timeString: string; endTimeString: string } | null {
      if (!this.startDate) return null
      const time = this.startTime || '09:00'
      const dateObject = new Date(this.startDate + 'T' + time + ':00')
      return {
        dateObject,
        dateString: this.startDate,
        timeString: time,
        endTimeString: time
      }
    },
    endDateTimeItem(): { dateObject: Date; dateString: string; timeString: string; endTimeString: string } | null {
      if (!this.endDate) return null
      const time = this.endTime || '09:30'
      const dateObject = new Date(this.endDate + 'T' + time + ':00')
      return {
        dateObject,
        dateString: this.endDate,
        timeString: time,
        endTimeString: time
      }
    },
    endDatePickerProps(): { locale?: string; min?: string } {
      return {
        locale: this.$vuetify.lang.current,
        min: this.startDate || undefined
      }
    },
    privateEventTooltipText(): string {
      const translated = this.$vuetify.lang.t('$vuetify.private_event_tooltip')
      return translated && translated !== '$vuetify.private_event_tooltip'
        ? translated
        : 'Une action privée n\'est visible que par son créateur ou les responsables de l\'action.'
    },
    addressPosition(): { latitude: number; longitude: number } | null {
      // Check form first (for new addresses being entered)
      if (this.form.address_position) {
        return {
          latitude: this.form.address_position.latitude,
          longitude: this.form.address_position.longitude,
        }
      }
      // Fallback to event address_position
      if (!this.event || !this.event.address_position) {
        return null
      }
      // Handle both formats: { lat, lng } and { latitude, longitude }
      const pos = this.event.address_position as any
      const lat = pos.lat ?? pos.latitude
      const lng = pos.lng ?? pos.longitude
      if (lat === undefined || lng === undefined || lat === null || lng === null) {
        return null
      }
      return {
        latitude: lat,
        longitude: lng,
      }
    },
    rules() {
      return {
        required: (v: any) => {
          if (!this.formSubmitted) return true
          return !!v || this.$vuetify.lang.t('$vuetify.required')
        },
        atLeastOneUser: (v: any[]) => {
          if (!this.formSubmitted) return true
          return (v && v.length > 0) || this.$vuetify.lang.t('$vuetify.at_least_one_user')
        },
        atLeastOneWorker: (v: any[]) => {
          if (!this.formSubmitted) return true
          return (v && v.length > 0) || this.$vuetify.lang.t('$vuetify.at_least_one_worker')
        },
        atLeastOneCompany: (v: any[]) => {
          if (!this.formSubmitted) return true
          return (v && v.length > 0) || this.$vuetify.lang.t('$vuetify.at_least_one_company')
        },
      }
    },
    selectedWorkersInListCount(): number {
      const workerIds = new Set(this.workers.map((w: any) => w.id))
      return (this.form.worker_ids || []).filter((id: number) => workerIds.has(id)).length
    },
    allWorkersSelected(): boolean {
      return this.workers.length > 0 &&
        this.selectedWorkersInListCount === this.workers.length
    },
    workersSelectionIndeterminate(): boolean {
      const count = this.selectedWorkersInListCount
      return count > 0 && count < this.workers.length
    },
    selectedCompaniesInListCount(): number {
      const companyIds = new Set(this.companies.map((c: any) => c.id))
      return (this.form.company_ids || []).filter((id: number) => companyIds.has(id)).length
    },
    allCompaniesSelected(): boolean {
      return this.companies.length > 0 &&
        this.selectedCompaniesInListCount === this.companies.length
    },
    companiesSelectionIndeterminate(): boolean {
      const count = this.selectedCompaniesInListCount
      return count > 0 && count < this.companies.length
    },
    selectedContactsInListCount(): number {
      const contactIds = new Set(this.contacts.map((c: any) => c.id))
      return (this.form.contact_ids || []).filter((id: number) => contactIds.has(id)).length
    },
    allContactsSelected(): boolean {
      return this.contacts.length > 0 &&
        this.selectedContactsInListCount === this.contacts.length
    },
    contactsSelectionIndeterminate(): boolean {
      const count = this.selectedContactsInListCount
      return count > 0 && count < this.contacts.length
    },
  },
  async mounted() {
    await this.fetchEventTypes()
    await this.fetchUsers()
    if (this.value) {
      this.categoryColors = getCategoryColors()
      this.initForm()
    }
  },
  watch: {
    value(newVal: boolean) {
      if (newVal) {
        this.categoryColors = getCategoryColors()
        this.formSubmitted = false
        this.showAddressMap = false
        this.initForm()
        // Reset form validation to clear any error states
        this.$nextTick(() => {
          const formRef = this.$refs.form as any
          formRef?.resetValidation()
          const cardText = this.$refs.cardText as HTMLElement
          cardText.scrollTop = 0
          // Ensure map is hidden after DOM update
          this.showAddressMap = false
        })
      } else {
        this.formSubmitted = false
        this.showAddressMap = false
        const formRef = this.$refs.form as any
        formRef?.resetValidation()
      }
    },
    startDate() {
      this.updateEndDateIfNeeded()
      if (this.isEditing) {
        this.ensureEndAfterStartIfInvalid()
      } else {
        this.updateEndTimeIfNeeded()
      }
    },
    startTime() {
      if (this.isEditing) {
        this.ensureEndAfterStartIfInvalid()
      } else {
        this.updateEndTimeIfNeeded()
      }
    },
    'form.type'(newType: string) {
      // Auto-fill title with type label if title is empty or was auto-filled
      if (newType && !this.titleManuallyEdited) {
        const typeLabel = this.$vuetify.lang.t(`$vuetify.event_type.${newType}`) as string
        this.form.title = typeLabel
      }
      // Update category color edit value
      if (this.selectedCategory) {
        this.categoryColorEdit = this.getCategoryColor(this.selectedCategory)
      }
      // Re-validate form when type changes (workers/companies requirements may change)
      this.$nextTick(() => {
        const form = this.$refs.form as any
        form?.validate()
      })
    },
    'form.title'(newTitle: string, oldTitle: string) {
      // Track if user manually edited the title
      if (newTitle && oldTitle !== newTitle) {
        // Check if the new title is different from any type label
        const typeLabels = this.eventTypeItems.map(item => item.text)
        if (!typeLabels.includes(newTitle)) {
          this.titleManuallyEdited = true
        }
      }
      if (!newTitle) {
        this.titleManuallyEdited = false
      }
    },
    'form.company_ids'(newIds: number[], oldIds: number[]) {
      // Auto-fill address when a company is selected (if address hasn't been manually edited)
      if (!this.addressManuallyEdited && newIds && newIds.length > 0) {
        const selectedCompanyId = newIds[newIds.length - 1] // Get the last selected company
        const company = this.companies.find((c: any) => c.id === selectedCompanyId)
        if (company && company.address) {
          this.form.address = company.address
        }
      }
      if (shouldSyncContactsOnCompanyChange(this.syncingCompanyContact)) {
        this.syncContactsFromCompanies(newIds, oldIds || [])
      }
    },
    'form.contact_ids'(newIds: number[]) {
      if (!this.syncingCompanyContact) {
        this.syncCompaniesFromContacts(newIds)
      }
    },
    'form.address'(newAddress: string, oldAddress: string) {
      // Track if user manually edited the address
      if (newAddress && oldAddress !== newAddress) {
        this.addressManuallyEdited = true
        // If address changed manually and doesn't match selectedAddress, clear selectedAddress
        if (!this.selectedAddress || this.selectedAddress.label !== newAddress) {
          this.selectedAddress = null
        }
      }
      if (!newAddress) {
        this.addressManuallyEdited = false
        this.selectedAddress = null
      }
    },
    selectedAgencyId() {
      // Changement d'espace : on rafraîchit les listes basées sur selectedAgencyId
      // sans jamais toucher aux IDs sélectionnés (form.user_ids / worker_ids / company_ids).
      this.fetchUsers()
      this.fetchInitialWorkers()
      this.fetchInitialCompanies()
      this.fetchInitialContacts()
    },
    showAddressMap(newVal: boolean) {
      if (newVal && this.addressPosition) {
        // Force re-render when map is shown
        this.addressMapDialogKey += 1
        // Ensure DOM is ready before map initializes
        this.$nextTick(() => {
          // Map will initialize via AddressPositionMap's mounted hook
        })
      }
    },
  },
  methods: {
    toggleSelectAllUsers() {
      if (this.allUsersSelected) {
        this.form.user_ids = []
      } else {
        this.form.user_ids = this.availableUsers.map((u) => u.id)
      }
    },
    async fetchEventTypes() {
      try {
        const response = await axios.get('/v1/calendar/events/types')
        this.eventTypesConfig = response.data
      } catch (error) {
        console.error('Failed to fetch event types:', error)
      }
    },
    getCategoryColor(category: EventCategory): string {
      return this.categoryColors[category] || '#9E9E9E'
    },
    updateCategoryColor(color: string) {
      if (this.selectedCategory) {
        const colors = getCategoryColors()
        colors[this.selectedCategory] = color
        localStorage.setItem('calendar_category_colors', JSON.stringify(colors))
        this.categoryColors = { ...getCategoryColors() }
        this.$emit('category-color-changed')
      }
    },
    async initForm() {
      // Always reset map visibility when initializing form
      this.showAddressMap = false

      if (this.event && this.event.id) {
        // Editing existing event - title was manually set
        this.titleManuallyEdited = true

        const startDateTime = this.parseDateTime(this.event.start_date)
        const endDateTime = this.parseDateTime(this.event.end_date)

        // Assign end before start so startDate/startTime watchers do not overwrite API end
        // (updateEndTimeIfNeeded forces start+30min when same day; skip that in edit via watchers).
        this.endDate = endDateTime.date
        this.endTime = endDateTime.time || '09:30'
        this.startDate = startDateTime.date
        this.startTime = startDateTime.time || '09:00'

        this.form = {
          title: this.event.title || '',
          type: this.event.type || '',
          description: this.event.description || '',
          address: this.event.address || '',
          result: this.event.result || '',
          is_private: this.event.is_private || false,
          user_ids: this.event.users?.map((u) => u.id) || [],
          worker_ids: this.event.workers?.map((w) => w.id) || [],
          company_ids: this.event.companies?.map((c) => c.id) || [],
          contact_ids: this.event.contacts?.map((c) => c.id) || [],
          deal_id: this.event.deal_id ?? this.event.deal?.id ?? null,
          notification_minutes_before: this.event.notification?.minutes_before || null,
        } as CalendarEventFormData
        
        // Set address_position if available
        if (this.event.address_position) {
          const pos = this.event.address_position as any
          const lat = pos.lat ?? pos.latitude
          const lng = pos.lng ?? pos.longitude
          if (lat !== undefined && lng !== undefined && lat !== null && lng !== null) {
            this.form.address_position = {
              latitude: lat,
              longitude: lng,
            }
          }
        }
        
        // Set selectedAddress for autocomplete display
        if (this.event.address) {
          this.selectedAddress = {
            label: this.event.address,
            coordinates: this.form.address_position ? [this.form.address_position.longitude, this.form.address_position.latitude] : undefined,
          }
        }
        
        // Address was set from event, so mark as manually edited
        this.addressManuallyEdited = !!this.event.address

        // Update category color edit
        if (this.selectedCategory) {
          this.categoryColorEdit = this.getCategoryColor(this.selectedCategory)
        }

        // Load associated workers and companies
        if (this.event.workers?.length) {
          this.workers = [...this.event.workers]
        }
        if (this.event.companies?.length) {
          this.companies = [...this.event.companies]
        }
        if (this.event.contacts?.length) {
          this.contacts = [...this.event.contacts]
        }
        if (this.event.deal) {
          this.deals = [this.event.deal]
        } else if (this.form.deal_id) {
          this.fetchDealById(this.form.deal_id)
        }
        
        // Load users for the selected agency
        this.fetchUsers()
        
        // Load initial workers, companies and contacts (200 entries)
        this.fetchInitialWorkers()
        this.fetchInitialCompanies()
        this.fetchInitialContacts()
        this.fetchInitialDeals()
        this.fetchInitialResultSuggestions()
      } else if (this.event) {
        // Creating new event with preselected date
        this.titleManuallyEdited = false

        const startDateTime = this.parseDateTime(this.event.start_date)
        const endDateTime = this.parseDateTime(this.event.end_date)

        this.startDate = startDateTime.date
        this.startTime = startDateTime.time || '09:00'
        
        // If end_date is not provided or is on a different day, use same date as start + 30 minutes
        if (!endDateTime.date || endDateTime.date !== startDateTime.date) {
          this.endDate = startDateTime.date
          // Calculate end time: start time + 30 minutes
          if (startDateTime.time) {
            const [hours, minutes] = startDateTime.time.split(':').map(Number)
            const startDateObj = new Date()
            startDateObj.setHours(hours, minutes, 0, 0)
            startDateObj.setMinutes(startDateObj.getMinutes() + 30)
            this.endTime = `${String(startDateObj.getHours()).padStart(2, '0')}:${String(startDateObj.getMinutes()).padStart(2, '0')}`
          } else {
            this.endTime = '09:30'
          }
        } else {
          this.endDate = endDateTime.date
          this.endTime = endDateTime.time || '09:30'
        }

        this.resetForm()
        this.addressManuallyEdited = false

        const currentUserId = getUserId()
        if (this.preselectedUserIds && this.preselectedUserIds.length > 0) {
          this.form.user_ids = [...this.preselectedUserIds]
        } else if (currentUserId && currentUserId !== 0) {
          this.form.user_ids = [Number(currentUserId)]
        }

        // Add preselected worker/company BEFORE fetches so merge logic preserves them
        if (this.preselectedWorker) {
          this.workers = [this.preselectedWorker]
          this.form.worker_ids = [this.preselectedWorker.id]
        }
        if (this.preselectedCompany) {
          this.companies = [this.preselectedCompany]
          this.form.company_ids = [this.preselectedCompany.id]
        }
        if (this.preselectedType) {
          this.form.type = this.preselectedType
        }
        if (this.preselectedTitle) {
          this.form.title = this.preselectedTitle
        }
        if (this.preselectedDescription) {
          this.form.description = this.preselectedDescription
        }
        if (this.preselectedAddress) {
          this.form.address = this.preselectedAddress
          this.selectedAddress = { label: this.preselectedAddress }
          this.addressManuallyEdited = true
        }
        if (this.preselectedResult) {
          this.form.result = this.preselectedResult
        }
        if (this.preselectedIsPrivate) {
          this.form.is_private = true
        }
        if (this.preselectedCompanyIds && this.preselectedCompanyIds.length > 0) {
          await this.fetchCompaniesByIds(this.preselectedCompanyIds)
        }
        if (this.preselectedWorkerIds && this.preselectedWorkerIds.length > 0) {
          await this.fetchWorkersByIds(this.preselectedWorkerIds)
        }
        await this.applyDealAndContactPrefills()

        // Load users for the selected agency
        this.fetchUsers()

        // Load initial workers and companies (200 entries)
        this.fetchInitialWorkers()
        this.fetchInitialCompanies()
        this.fetchInitialContacts()
        this.fetchInitialDeals()
        this.fetchInitialResultSuggestions()
      } else {
        // Creating new event from scratch
        this.titleManuallyEdited = false

        const now = new Date()
        if (this.preselectedStartDate) {
          this.startDate = this.preselectedStartDate
          this.startTime = this.preselectedStartTime || '09:00'
          this.endDate = this.preselectedEndDate || this.startDate
          this.endTime = this.preselectedEndTime || '09:30'
        } else {
          this.startDate = now.toISOString().split('T')[0]
          this.startTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
          const endNow = new Date(now.getTime() + 30 * 60 * 1000)
          this.endDate = endNow.toISOString().split('T')[0]
          this.endTime = `${String(endNow.getHours()).padStart(2, '0')}:${String(endNow.getMinutes()).padStart(2, '0')}`
        }

        this.resetForm()

        const currentUserId = getUserId()
        if (this.preselectedUserIds && this.preselectedUserIds.length > 0) {
          this.form.user_ids = [...this.preselectedUserIds]
        } else if (currentUserId && currentUserId !== 0) {
          this.form.user_ids = [Number(currentUserId)]
        }

        // Add preselected worker/company BEFORE fetches so merge logic preserves them
        if (this.preselectedWorker) {
          this.workers = [this.preselectedWorker]
          this.form.worker_ids = [this.preselectedWorker.id]
        }
        if (this.preselectedCompany) {
          this.companies = [this.preselectedCompany]
          this.form.company_ids = [this.preselectedCompany.id]
        }
        if (this.preselectedType) {
          this.form.type = this.preselectedType
        }
        if (this.preselectedTitle) {
          this.form.title = this.preselectedTitle
        }
        if (this.preselectedDescription) {
          this.form.description = this.preselectedDescription
        }
        if (this.preselectedAddress) {
          this.form.address = this.preselectedAddress
          this.selectedAddress = { label: this.preselectedAddress }
          this.addressManuallyEdited = true
        }
        if (this.preselectedResult) {
          this.form.result = this.preselectedResult
        }
        if (this.preselectedIsPrivate) {
          this.form.is_private = true
        }
        if (this.preselectedCompanyIds && this.preselectedCompanyIds.length > 0) {
          await this.fetchCompaniesByIds(this.preselectedCompanyIds)
        }
        if (this.preselectedWorkerIds && this.preselectedWorkerIds.length > 0) {
          await this.fetchWorkersByIds(this.preselectedWorkerIds)
        }
        await this.applyDealAndContactPrefills()

        // Load users for the selected agency (preselectedUserIds applied above)
        this.fetchUsers()

        // Load initial workers, companies and contacts (200 entries)
        this.fetchInitialWorkers()
        this.fetchInitialCompanies()
        this.fetchInitialContacts()
        this.fetchInitialDeals()
        this.fetchInitialResultSuggestions()
      }
    },
    async applyDealAndContactPrefills() {
      if (this.preselectedDealId) {
        this.form.deal_id = Number(this.preselectedDealId)
        await this.fetchDealById(this.form.deal_id)
      }
      if (this.preselectedContactIds && this.preselectedContactIds.length > 0) {
        await this.fetchContactsByIds(this.preselectedContactIds)
      }
    },
    resetForm() {
      this.formSubmitted = false
      this.showAddressMap = false
      this.selectedAddress = null
      this.addressSuggestions = []
      this.form = {
        title: '',
        type: '',
        description: '',
        address: '',
        result: '',
        is_private: false,
        user_ids: [],
        address_position: null,
        worker_ids: [],
        company_ids: [],
        contact_ids: [],
        deal_id: null,
        notification_minutes_before: null,
      } as CalendarEventFormData
      this.addressManuallyEdited = false
      this.workers = []
      this.companies = []
      this.contacts = []
      this.deals = []
      // Reset form validation
      this.$nextTick(() => {
        const formRef = this.$refs.form as any
        formRef?.resetValidation()
      })
    },
    parseDateTime(dateStr: string): { date: string; time: string } {
      if (!dateStr) {
        return { date: '', time: '' }
      }
      const normalized = dateStr.replace(' ', 'T')
      const parts = normalized.split('T')
      return {
        date: parts[0] || '',
        time: parts[1]?.substring(0, 5) || '',
      }
    },
    updateEndDateIfNeeded() {
      if (!this.startDate) {
        return
      }
      if (!this.endDate || this.endDate < this.startDate) {
        this.endDate = this.startDate
      }
    },
    /**
     * Create flow: when start and end share the same day, set end to start+30 only if end is not
     * already after start (preserves API/calendar preselection e.g. 10:00–11:00; avoids pickers
     * re-emitting start and wiping a valid end).
     */
    updateEndTimeIfNeeded() {
      if (this.startDate !== this.endDate || !this.startDate) {
        return
      }
      const startMs = new Date(`${this.startDate}T${this.startTime || '00:00'}:00`).getTime()
      const endMs = new Date(`${this.endDate}T${this.endTime || '00:00'}:00`).getTime()
      const thirtyMinMs = 30 * 60 * 1000
      if (endMs >= startMs + thirtyMinMs) {
        return
      }
      if (endMs > startMs) {
        const startHour = Number((this.startTime || '00:00').split(':')[0])
        // Short slot after start: keep (e.g. PA 10:00–10:15). Late evening: likely stale "now+30" → recompute.
        if (startHour < 23) {
          return
        }
      }
      const [startHour, startMinute] = (this.startTime || '09:00').split(':').map(Number)
      const startMinutes = startHour * 60 + startMinute
      const newEndMinutes = startMinutes + 30
      const endHour = Math.floor(newEndMinutes / 60) % 24
      const endMinute = newEndMinutes % 60
      this.endTime = `${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`
    },
    /**
     * Edit flow: do not replace the event's end with start+30. Only fix when end <= start (invalid).
     */
    ensureEndAfterStartIfInvalid() {
      if (!this.startDate || !this.endDate) {
        return
      }
      const startMs = new Date(`${this.startDate}T${this.startTime || '00:00'}:00`).getTime()
      const endMs = new Date(`${this.endDate}T${this.endTime || '00:00'}:00`).getTime()
      if (endMs > startMs) {
        return
      }
      const fixed = new Date(startMs + 30 * 60 * 1000)
      const y = fixed.getFullYear()
      const m = String(fixed.getMonth() + 1).padStart(2, '0')
      const d = String(fixed.getDate()).padStart(2, '0')
      const h = String(fixed.getHours()).padStart(2, '0')
      const min = String(fixed.getMinutes()).padStart(2, '0')
      this.endDate = `${y}-${m}-${d}`
      this.endTime = `${h}:${min}`
    },
    onStartDateTimeInput(val: any) {
      if (!val || !val.dateString) {
        this.startDate = ''
        this.startTime = '09:00'
        return
      }
      this.startDate = val.dateString
      this.startTime = (val.timeString || '09:00').substring(0, 5)
      this.updateEndDateIfNeeded()
      this.updateEndTimeIfNeeded()
    },
    onEndDateTimeInput(val: any) {
      if (!val || !val.dateString) {
        this.endDate = ''
        this.endTime = '09:30'
        return
      }
      this.endDate = val.dateString
      this.endTime = (val.timeString || '09:30').substring(0, 5)
      this.updateEndDateIfNeeded()
    },
    parseStartDate() {
      // Parse various date formats: DD/MM/YYYY, YYYY-MM-DD, DD-MM-YYYY
      const dateStr = this.startDate.trim()
      if (!dateStr) return
      
      let parsedDate: Date | null = null
      
      // Try DD/MM/YYYY or DD-MM-YYYY
      const ddmmyyyy = dateStr.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/)
      if (ddmmyyyy) {
        const [, day, month, year] = ddmmyyyy
        parsedDate = new Date(Number.parseInt(year, 10), Number.parseInt(month, 10) - 1, Number.parseInt(day, 10))
      }
      
      // Try YYYY-MM-DD
      if (!parsedDate) {
        const yyyymmdd = dateStr.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/)
        if (yyyymmdd) {
          const [, year, month, day] = yyyymmdd
          parsedDate = new Date(Number.parseInt(year, 10), Number.parseInt(month, 10) - 1, Number.parseInt(day, 10))
        }
      }
      
      if (parsedDate && !Number.isNaN(parsedDate.getTime())) {
        this.startDate = parsedDate.toISOString().split('T')[0]
      }
    },
    parseStartTime() {
      // Parse time formats: HH:mm, HH:mm:ss, H:mm
      const timeStr = this.startTime.trim()
      if (!timeStr) return
      
      const timeMatch = timeStr.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/)
      if (timeMatch) {
        const [, hour, minute] = timeMatch
        const h = Number.parseInt(hour, 10)
        const m = Number.parseInt(minute, 10)
        if (h >= 0 && h < 24 && m >= 0 && m < 60) {
          this.startTime = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
        }
      }
    },
    parseEndDate() {
      // Parse various date formats: DD/MM/YYYY, YYYY-MM-DD, DD-MM-YYYY
      const dateStr = this.endDate.trim()
      if (!dateStr) return
      
      let parsedDate: Date | null = null
      
      // Try DD/MM/YYYY or DD-MM-YYYY
      const ddmmyyyy = dateStr.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/)
      if (ddmmyyyy) {
        const [, day, month, year] = ddmmyyyy
        parsedDate = new Date(Number.parseInt(year, 10), Number.parseInt(month, 10) - 1, Number.parseInt(day, 10))
      }
      
      // Try YYYY-MM-DD
      if (!parsedDate) {
        const yyyymmdd = dateStr.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/)
        if (yyyymmdd) {
          const [, year, month, day] = yyyymmdd
          parsedDate = new Date(Number.parseInt(year, 10), Number.parseInt(month, 10) - 1, Number.parseInt(day, 10))
        }
      }
      
      if (parsedDate && !Number.isNaN(parsedDate.getTime())) {
        this.endDate = parsedDate.toISOString().split('T')[0]
        this.updateEndDateIfNeeded()
      }
    },
    parseEndTime() {
      // Parse time formats: HH:mm, HH:mm:ss, H:mm
      const timeStr = this.endTime.trim()
      if (!timeStr) return
      
      const timeMatch = timeStr.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/)
      if (timeMatch) {
        const [, hour, minute] = timeMatch
        const h = Number.parseInt(hour, 10)
        const m = Number.parseInt(minute, 10)
        if (h >= 0 && h < 24 && m >= 0 && m < 60) {
          this.endTime = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
        }
      }
    },
    workerLabel(item: any): string {
      return `${item.first_name} ${item.last_name}`
    },
    contactLabel(item: any): string {
      const name = [item.first_name, item.last_name].filter(Boolean).join(' ').trim()
      return name || item.email || String(item.id)
    },
    selectAllUsers() {
      this.form.user_ids = this.availableUsers.map((u) => u.id)
    },
    deselectAllUsers() {
      this.form.user_ids = []
    },
    removeUserFromFilter(userId: number) {
      this.form.user_ids = this.form.user_ids.filter((id: number) => id !== userId)
    },
    toggleSelectAllWorkers() {
      if (this.allWorkersSelected) {
        this.deselectAllWorkers()
      } else {
        this.selectAllWorkers()
      }
    },
    selectAllWorkers() {
      this.form.worker_ids = this.workers.map((w: any) => w.id)
    },
    deselectAllWorkers() {
      this.form.worker_ids = []
    },
    removeWorkerFromFilter(workerId: number) {
      this.form.worker_ids = this.form.worker_ids.filter((id: number) => id !== workerId)
    },
    toggleSelectAllCompanies() {
      if (this.allCompaniesSelected) {
        this.deselectAllCompanies()
      } else {
        this.selectAllCompanies()
      }
    },
    selectAllCompanies() {
      this.form.company_ids = this.companies.map((c: any) => c.id)
    },
    deselectAllCompanies() {
      this.form.company_ids = []
    },
    removeCompanyFromFilter(companyId: number) {
      this.form.company_ids = this.form.company_ids.filter((id: number) => id !== companyId)
    },
    toggleSelectAllContacts() {
      if (this.allContactsSelected) {
        this.deselectAllContacts()
      } else {
        this.selectAllContacts()
      }
    },
    selectAllContacts() {
      this.form.contact_ids = this.contacts.map((c: any) => c.id)
    },
    deselectAllContacts() {
      this.form.contact_ids = []
    },
    removeContactFromFilter(contactId: number) {
      this.form.contact_ids = this.form.contact_ids.filter((id: number) => id !== contactId)
    },
    getSelectedContactIds(): number[] {
      return this.form.contact_ids || []
    },
    getSelectedCompanyIds(): number[] {
      return this.form.company_ids || []
    },
    assignContactIdsAfterCompanyRemoval(removedCompanyIds: number[]) {
      this.form.contact_ids = filterContactIdsAfterCompanyRemoval(
        this.getSelectedContactIds(),
        this.contacts,
        removedCompanyIds,
      )
    },
    assignContactIdsFromSync(newContactIds: number[]) {
      this.form.contact_ids = mergeContactIds(this.getSelectedContactIds(), newContactIds)
    },
    async fetchUsers() {
      this.loadingUsers = true
      try {
        const params = this.buildFetchUsersParams()
        const previousLocalUsers = [...this.localUsers]
        const selectedUserIds = new Set(this.form.user_ids)

        const response = await axios.get('/v1/space/users', { params })
        const usersData = Array.isArray(response.data) ? response.data : (response.data.data || [])

        const numId = this.getCurrentUserIdAsNumber()
        const meLabel = this.$vuetify.lang.t('$vuetify.me') as string
        const lastName = (u: any) => String(u.lastname ?? u.last_name ?? '').trim()
        const firstName = (u: any) => String(u.firstname ?? u.first_name ?? '').trim()
        const sortByLastFirst = (a: any, b: any) => {
          const cmpLast = lastName(a).localeCompare(lastName(b), undefined, { sensitivity: 'base' })
          if (cmpLast !== 0) return cmpLast
          return firstName(a).localeCompare(firstName(b), undefined, { sensitivity: 'base' })
        }

        const fetchedUsers = this.mapUsersWithDisplayName(usersData, numId, meLabel, lastName, firstName)
        const mergedById = new Map<number, any>()
        fetchedUsers.forEach((u: any) => mergedById.set(u.id, u))

        this.mergeEventUsersIntoMap(mergedById, numId, meLabel, lastName, firstName)
        this.mergeSelectedUsersFromCandidates(mergedById, previousLocalUsers, selectedUserIds)
        this.localUsers = this.buildSortedUserList(mergedById, numId, meLabel, lastName, firstName, sortByLastFirst)
      } catch (error) {
        console.error('Failed to fetch users:', error)
      } finally {
        this.loadingUsers = false
      }
    },
    buildFetchUsersParams(): Record<string, any> {
      const params: any = { per_page: 0 }
      const agencyId = this.selectedAgencyId || state.agency
      if (agencyId) params.spaces = agencyId
      return params
    },
    getCurrentUserIdAsNumber(): number | null {
      const currentUserId = getUserId()
      return currentUserId ? Number(currentUserId) : null
    },
    mapUsersWithDisplayName(
      usersData: any[],
      numId: number | null,
      meLabel: string,
      lastName: (u: any) => string,
      firstName: (u: any) => string
    ): any[] {
      return usersData.map((user: any) => {
        const ln = lastName(user)
        const fn = firstName(user)
        const fullName = [ln, fn].filter(Boolean).join(' ')
        const isCurrentUser = !!(numId && Number(user.id) === numId)
        const displayName = getUserDisplayName(isCurrentUser, fullName, meLabel)
        return { ...user, lastname: ln, firstname: fn, full_name: displayName }
      })
    },
    mergeEventUsersIntoMap(
      mergedById: Map<number, any>,
      numId: number | null,
      meLabel: string,
      lastName: (u: any) => string,
      firstName: (u: any) => string
    ) {
      if (!this.isEditing || !this.event) return
      if (this.event.users?.length) {
        this.event.users.forEach((user: any) => {
          const existing = mergedById.get(user.id)
          const normalized = {
            id: user.id,
            firstname: user.first_name || user.firstname || '',
            lastname: user.last_name || user.lastname || '',
            full_name: `${user.first_name || user.firstname || ''} ${user.last_name || user.lastname || ''}`.trim(),
            email: user.email || existing?.email || '',
          }
          mergedById.set(normalized.id, existing ? { ...existing, ...normalized } : normalized)
        })
      }
      if (!this.event.user_id || mergedById.has(this.event.user_id)) return
      const creatorInUsers = this.event.users?.find((u: any) => Number(u.id) === Number(this.event!.user_id)) as { first_name?: string; last_name?: string; email?: string } | undefined
      let fn = creatorInUsers?.first_name || this.event.user_first_name || ''
      let ln = creatorInUsers?.last_name || this.event.user_last_name || ''
      if (!fn && !ln) {
        const fromProp = (this.users as any[]).find((u: any) => Number(u.id) === Number(this.event!.user_id))
        if (fromProp?.full_name) {
          fn = ''
          ln = fromProp.full_name
        } else if (fromProp) {
          fn = fromProp.firstname ?? ''
          ln = fromProp.lastname ?? ''
        }
      }
      const fullName = [ln, fn].filter(Boolean).join(' ')
      const isCurrentUser = !!(numId && Number(this.event.user_id) === numId)
      const displayName = getUserDisplayName(isCurrentUser, fullName, meLabel)
      const existingCreator = mergedById.get(this.event.user_id)
      mergedById.set(this.event.user_id, {
        ...existingCreator,
        id: this.event.user_id,
        firstname: fn,
        lastname: ln,
        full_name: displayName,
        email: creatorInUsers?.email || existingCreator?.email || '',
      })
    },
    mergeSelectedUsersFromCandidates(
      mergedById: Map<number, any>,
      previousLocalUsers: any[],
      selectedUserIds: Set<number>
    ) {
      const candidatesForSelection = [...previousLocalUsers, ...(this.users as any[])]
      const candidatesById = new Map<number, any>()
      candidatesForSelection.forEach((u: any) => {
        if (u && typeof u.id === 'number') {
          candidatesById.set(u.id, {
            ...u,
            full_name: u.full_name || `${u.firstname || u.first_name || ''} ${u.lastname || u.last_name || ''}`.trim(),
          })
        }
      })
      selectedUserIds.forEach((id) => {
        if (!mergedById.has(id) && candidatesById.has(id)) {
          mergedById.set(id, candidatesById.get(id))
        }
      })
    },
    buildSortedUserList(
      mergedById: Map<number, any>,
      numId: number | null,
      meLabel: string,
      lastName: (u: any) => string,
      firstName: (u: any) => string,
      sortByLastFirst: (a: any, b: any) => number
    ): any[] {
      const allUsers = Array.from(mergedById.values()).map((u: any) => {
        if (numId && Number(u.id) === numId) {
          const ln = lastName(u)
          const fn = firstName(u)
          const fullName = [ln, fn].filter(Boolean).join(' ')
          const displayName = getUserDisplayName(true, fullName, meLabel)
          return { ...u, full_name: displayName }
        }
        return u
      })
      const others = allUsers.filter((u: any) => Number(u.id) !== numId).sort(sortByLastFirst)
      return numId
        ? [...allUsers.filter((u: any) => Number(u.id) === numId), ...others]
        : allUsers.sort(sortByLastFirst)
    },
    async fetchWorkers(search?: string) {
      this.loadingWorkers = true
      try {
        const params: any = {
          per_page: 200,
          deleted_at: false,
          fields: 'id,first_name,last_name',
        }
        if (search && search.length >= 2) {
          params.names = search
        }

        // L'espace courant pour le filtrage des workers
        const agencyId = this.selectedAgencyId || state.agency
        if (agencyId) {
          params.agency_id = agencyId
        }

        const previousWorkers = [...this.workers]
        const selectedWorkerIds = new Set(this.form.worker_ids)

        const response = await axios.get('/v1/workers', {
          params,
        })
        const raw = response.data.data ?? response.data
        const apiWorkers = Array.isArray(raw) ? raw : []

        const mergedById = new Map<number, any>()

        // Selected workers first (base), then API overwrites when present
        previousWorkers.forEach((w: any) => {
          if (w && typeof w.id === 'number' && selectedWorkerIds.has(w.id)) {
            mergedById.set(w.id, w)
          }
        })
        apiWorkers.filter((w: any) => w && typeof w.id === 'number').forEach((w: any) => {
          mergedById.set(w.id, w)
        })

        this.workers = Array.from(mergedById.values())
      } catch (error) {
        console.error('Failed to fetch workers:', error)
      } finally {
        this.loadingWorkers = false
      }
    },
    async fetchInitialWorkers() {
      await this.fetchWorkers()
    },
    async fetchWorkersByIds(ids: number[]) {
      if (!ids || ids.length === 0) return
      this.loadingWorkers = true
      try {
        const params: CalendarApiQueryParams = {
          id: ids.join(','),
          deleted_at: false,
          per_page: 0,
          fields: 'id,first_name,last_name',
        }
        // Ne pas filtrer par agency_id : l'espace sélectionné dans l'UI peut différer de celui
        // des PA / du calendrier et exclurait des personnels pourtant accessibles (filtrage user côté API).
        const response = await axios.get('/v1/workers', { params })
        const apiWorkers = response.data.data ?? response.data
        const fetched = Array.isArray(apiWorkers) ? apiWorkers : []
        const normalized = fetched
          .map((w: any) => {
            if (!w || w.id === undefined || w.id === null) return null
            const id = Number(w.id)
            if (!Number.isFinite(id)) return null
            return { ...w, id }
          })
          .filter(Boolean) as any[]
        this.workers = normalized
        const idSet = new Set(normalized.map((w: any) => w.id))
        this.form.worker_ids = ids.filter((id) => idSet.has(id))
      } catch (error) {
        console.error('Failed to fetch workers by ids:', error)
      } finally {
        this.loadingWorkers = false
      }
    },
    searchWorkers: debounce(async function (this: any, search: string) {
      if (!search || search.length < 2) return
      await this.fetchWorkers(search)
    }, 300),
    async fetchCompanies(search?: string) {
      this.loadingCompanies = true
      try {
        const params: any = {
          per_page: 200,
          deleted_at: false,
          fields: 'id,name',
        }
        if (search && search.length >= 2) {
          params.name = search
        }

        // L'espace courant pour le filtrage des entreprises
        const agencyId = this.selectedAgencyId || state.agency
        if (agencyId) {
          params.space_id = agencyId
        }

        const previousCompanies = [...this.companies]
        const selectedCompanyIds = new Set(this.form.company_ids)

        const response = await axios.get('/v1/company', {
          params,
        })
        const raw = response.data.data ?? response.data
        const apiCompanies = Array.isArray(raw) ? raw : []

        const mergedById = new Map<number, any>()

        // Selected companies first (base), then API overwrites when present
        previousCompanies.forEach((c: any) => {
          if (c && typeof c.id === 'number' && selectedCompanyIds.has(c.id)) {
            mergedById.set(c.id, c)
          }
        })
        apiCompanies.filter((c: any) => c && typeof c.id === 'number').forEach((c: any) => {
          mergedById.set(c.id, c)
        })

        this.companies = Array.from(mergedById.values())
      } catch (error) {
        console.error('Failed to fetch companies:', error)
      } finally {
        this.loadingCompanies = false
      }
    },
    async fetchInitialCompanies() {
      await this.fetchCompanies()
    },
    async fetchCompaniesByIds(ids: number[]) {
      if (!ids || ids.length === 0) return
      this.loadingCompanies = true
      try {
        const params: CalendarApiQueryParams = {
          id: ids.join(','),
          deleted_at: false,
          per_page: 0,
        }
        const response = await axios.get('/v1/company', { params })
        const raw = response.data.data ?? response.data
        const fetched = Array.isArray(raw) ? raw : []
        this.companies = fetched.filter((c: any) => c && typeof c.id === 'number')
        this.form.company_ids = this.companies.map((c: any) => c.id)
      } catch (error) {
        console.error('Failed to fetch companies by ids:', error)
      } finally {
        this.loadingCompanies = false
      }
    },
    searchCompanies: debounce(async function (this: any, search: string | null | undefined) {
      const s = search == null ? '' : String(search)
      if (s.length >= 2) {
        await this.fetchCompanies(s)
      } else {
        await this.fetchCompanies()
      }
    }, 300),
    mergeContactsIntoList(fetchedContacts: any[]) {
      this.contacts = mergeContactsBySelectedIds(
        this.contacts,
        fetchedContacts,
        this.getSelectedContactIds(),
      )
    },
    async fetchContacts(search?: string) {
      this.loadingContacts = true
      try {
        const params: CalendarApiQueryParams = {
          per_page: 200,
        }
        if (search && search.length >= 2) {
          params.searches = search
        }
        const selectedCompanyIds = (this.form.company_ids || []).filter((id: number) => Number.isFinite(id))
        if (selectedCompanyIds.length > 0) {
          params.company_id = selectedCompanyIds.join(',')
        }

        const response = await axios.get('/v1/contacts', { params })
        const apiContacts = parseContactsListResponse(response.data)

        this.contacts = mergeContactsBySelectedIds(
          this.contacts,
          apiContacts,
          this.getSelectedContactIds(),
        )
      } catch (error) {
        console.error('Failed to fetch contacts:', error)
      } finally {
        this.loadingContacts = false
      }
    },
    async fetchInitialContacts() {
      await this.fetchContacts()
    },
    dealLabel(deal: { id?: number; title?: string | null }): string {
      if (!deal) return ''
      const title = String(deal.title || '').trim()
      if (title) return title
      return deal.id != null ? String(deal.id) : ''
    },
    async fetchDeals(search?: string) {
      this.loadingDeals = true
      try {
        const params: CalendarApiQueryParams = {
          status: 'opened',
          page: 1,
          per_page: 50,
        }
        const agencyId = this.selectedAgencyId || state.agency
        if (agencyId) {
          params.space_id = Number(agencyId)
        }
        if (search && search.length >= 2) {
          params.searches = search
        }
        const previousDeals = [...this.deals]
        const selectedDealId = this.form.deal_id
        const response = await axios.get('/v1/deals', { params })
        const raw = response.data?.data ?? response.data
        const apiDeals = Array.isArray(raw) ? raw : []
        const mergedById = new Map<number, any>()
        previousDeals.forEach((deal: any) => {
          if (deal && typeof deal.id === 'number' && selectedDealId === deal.id) {
            mergedById.set(deal.id, deal)
          }
        })
        apiDeals.forEach((deal: any) => {
          if (deal && deal.id != null) {
            const id = Number(deal.id)
            if (Number.isFinite(id)) {
              mergedById.set(id, { ...deal, id })
            }
          }
        })
        this.deals = Array.from(mergedById.values())
      } catch (error) {
        console.error('Failed to fetch deals:', error)
      } finally {
        this.loadingDeals = false
      }
    },
    async fetchInitialDeals() {
      await this.fetchDeals()
    },
    async fetchDealById(dealId: number) {
      if (!dealId) return
      this.loadingDeals = true
      try {
        const response = await axios.get(`/v1/deals/${dealId}`)
        const deal = response.data
        if (deal && deal.id != null) {
          const normalized = { ...deal, id: Number(deal.id) }
          const others = this.deals.filter((item: any) => Number(item.id) !== normalized.id)
          this.deals = [normalized, ...others]
          this.form.deal_id = normalized.id
        }
      } catch (error) {
        console.error('Failed to fetch deal by id:', error)
      } finally {
        this.loadingDeals = false
      }
    },
    searchDeals: debounce(async function (this: any, search: string | null | undefined) {
      const s = search == null ? '' : String(search)
      if (s.length >= 2) {
        await this.fetchDeals(s)
      } else {
        await this.fetchDeals()
      }
    }, 300),
    async fetchContactsByIds(ids: number[]) {
      if (!ids || ids.length === 0) return
      this.loadingContacts = true
      try {
        const params: CalendarApiIdQueryParams = {
          id: ids.join(','),
          per_page: 0,
        }
        const response = await axios.get('/v1/contacts', { params })
        const fetched = parseContactsListResponse(response.data)
        const normalized = fetched
          .map((c: any) => normalizeContactRecord(c))
          .filter(Boolean) as any[]
        this.mergeContactsIntoList(normalized)
        const idSet = new Set(normalized.map((c: any) => c.id))
        this.form.contact_ids = ids.filter((id) => idSet.has(id))
      } catch (error) {
        console.error('Failed to fetch contacts by ids:', error)
      } finally {
        this.loadingContacts = false
      }
    },
    async fetchContactsForCompanies(companyIds: number[]): Promise<any[]> {
      if (!companyIds || companyIds.length === 0) return []
      try {
        const params: CalendarApiIdQueryParams = {
          company_id: companyIds.join(','),
          per_page: 0,
        }
        const response = await axios.get('/v1/contacts', { params })
        return filterContactsWithNumericId(parseContactsListResponse(response.data))
      } catch (error) {
        console.error('Failed to fetch contacts for companies:', error)
        return []
      }
    },
    async mergeCompaniesByIds(companyIds: number[]) {
      if (!companyIds || companyIds.length === 0) return
      const missingIds = companyIds.filter((id) => !this.companies.some((c: any) => c.id === id))
      if (missingIds.length === 0) return
      try {
        const params: CalendarApiQueryParams = {
          id: missingIds.join(','),
          deleted_at: false,
          per_page: 0,
        }
        const response = await axios.get('/v1/company', { params })
        const fetched = parseCompaniesListResponse(response.data)
        this.companies = mergeCompaniesById(this.companies, fetched)
      } catch (error) {
        console.error('Failed to merge companies by ids:', error)
      }
    },
    async syncCompaniesFromContacts(contactIds: number[]) {
      const companyIdsFromContacts = getCompanyIdsFromSelectedContacts(this.contacts, contactIds)
      const previousCompanyIds = this.getSelectedCompanyIds()
      const nextCompanyIds = buildNextCompanyIds(previousCompanyIds, companyIdsFromContacts)

      if (!shouldSyncCompanyIds(previousCompanyIds, nextCompanyIds)) {
        return
      }

      this.syncingCompanyContact = true
      try {
        await this.mergeCompaniesByIds(companyIdsFromContacts)
        this.form.company_ids = nextCompanyIds
      } finally {
        this.syncingCompanyContact = false
      }
    },
    async syncContactsFromCompanies(newCompanyIds: number[], oldCompanyIds: number[]) {
      const { added, removed } = getAddedAndRemovedIds(newCompanyIds, oldCompanyIds)

      if (added.length === 0 && removed.length === 0) {
        await this.fetchContacts(this.contactSearch || undefined)
        return
      }

      this.syncingCompanyContact = true
      try {
        if (removed.length > 0) {
          this.assignContactIdsAfterCompanyRemoval(removed)
        }

        if (added.length > 0) {
          const newContacts = await this.fetchContactsForCompanies(added)
          this.mergeContactsIntoList(newContacts)
          this.assignContactIdsFromSync(newContacts.map((c: any) => c.id))
        }

        await this.fetchContacts(this.contactSearch || undefined)
      } finally {
        this.syncingCompanyContact = false
      }
    },
    searchContacts: debounce(async function (this: any, search: string | null | undefined) {
      const s = search == null ? '' : String(search)
      if (s.length >= 2) {
        await this.fetchContacts(s)
      } else {
        await this.fetchContacts()
      }
    }, 300),
    async fetchResultSuggestions(search?: string) {
      this.loadingResults = true
      try {
        const params: any = {
          data: 'event_result',
        }
        // Get agency/space id from selection if available, otherwise from state
        const agencyId = this.selectedAgencyId || state.agency
        if (agencyId) {
          params.space_id = agencyId
        }
        if (search && search.length >= 2) {
          params.search = search
        }
        const response = await axios.get('/v1/autocomplete', { params })
        const suggestions = Array.isArray(response.data) ? response.data : []
        // Merge with existing to keep selected ones
        const existingSuggestions = new Set(this.resultSuggestions)
        this.resultSuggestions = [
          ...this.resultSuggestions,
          ...suggestions.filter((s: string) => !existingSuggestions.has(s)),
        ]
      } catch (error) {
        console.error('Failed to fetch result suggestions:', error)
      } finally {
        this.loadingResults = false
      }
    },
    searchAddresses: debounce(async function (this: any, search: string) {
      if (!search || search.length < 3) {
        this.addressSuggestions = []
        return
      }
      this.loadingAddresses = true
      try {
        const response = await fetch(
          `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(search)}&limit=10`
        )
        if (response.ok) {
          const data = await response.json()
          this.addressSuggestions = (data.features || []).map((feature: any) => ({
            label: feature.properties.label,
            city: feature.properties.city,
            postcode: feature.properties.postcode,
            coordinates: feature.geometry?.coordinates || null,
          }))
        } else {
          this.addressSuggestions = []
        }
      } catch (error) {
        console.error('Error fetching addresses:', error)
        this.addressSuggestions = []
      } finally {
        this.loadingAddresses = false
      }
    }, 300),
    onAddressInput(value: any) {
      // Handle manual typing - update form.address when user types
      if (typeof value === 'string') {
        this.form.address = value
        if (value && this.selectedAddress && this.selectedAddress.label !== value) {
          // User is typing something different, clear selection
          this.selectedAddress = null
          this.form.address_position = null
        }
      }
    },
    onAddressSelected(address: any) {
      if (address && typeof address === 'object' && address.coordinates) {
        // Address selected from autocomplete
        const [longitude, latitude] = address.coordinates
        this.form.address = address.label || ''
        this.form.address_position = {
          latitude,
          longitude,
        }
        this.addressManuallyEdited = true
      } else if (!address) {
        // Field cleared
        this.form.address = ''
        this.form.address_position = null
        this.addressManuallyEdited = true
        this.selectedAddress = null
      }
    },
    async fetchInitialResultSuggestions() {
      await this.fetchResultSuggestions()
    },
    handleResultSearchInput(search: string) {
      // Update form.result immediately when user types
      if (search !== null && search !== undefined) {
        this.form.result = search
        // Trigger search after debounce
        this.searchResults(search)
      }
    },
    searchResults: debounce(async function (this: any, search: string) {
      if (!search || search.length < 2) return
      await this.fetchResultSuggestions(search)
    }, 300),
    handleResultBlur() {
      // Add the current value to suggestions when user leaves the input, if it doesn't exist
      if (this.form.result && this.form.result.trim() !== '' && !this.resultSuggestions.includes(this.form.result)) {
        this.resultSuggestions.push(this.form.result)
      }
    },
    async save() {
      this.formSubmitted = true
      const formRef = this.$refs.form as any
      if (!formRef.validate()) {
        // Scroll to first error field
        this.$nextTick(() => {
          const cardText = this.$refs.cardText as HTMLElement
          const errorElement = cardText.querySelector('.error--text, .v-input--error-state input, .v-input--error-state textarea, .v-input--error-state .v-select')
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
          } else {
            cardText.scrollTop = 0
          }
        })
        return
      }

      // Validate dates (DateTimePicker2 doesn't feed v-form rules)
      if (!this.startDate || !this.endDate) {
        EventBus.$emit('snackbar', {
          message: this.$vuetify.lang.t('$vuetify.required'),
          color: 'error',
        })
        return
      }
      const startDateTime = `${this.startDate} ${this.startTime || '09:00'}:00`
      const endDateTime = `${this.endDate} ${this.endTime || '09:30'}:00`

      if (new Date(endDateTime) <= new Date(startDateTime)) {
        EventBus.$emit('snackbar', {
          message: this.$vuetify.lang.t('$vuetify.end_date_must_be_after_start'),
          color: 'error',
        })
        return
      }

      this.saving = true
      try {
        // Add current result to suggestions if it's a new value
        if (this.form.result && this.form.result.trim() !== '' && !this.resultSuggestions.includes(this.form.result)) {
          this.resultSuggestions.push(this.form.result)
        }

        const payload = {
          ...this.form,
          start_date: startDateTime,
          end_date: endDateTime,
        }

        if (this.isEditing) {
          await axios.put(`/v1/calendar/events/${this.event!.id}`, payload)
          EventBus.$emit('snackbar', { message: this.$vuetify.lang.t('$vuetify.calendar_event_updated') })
        } else {
          await axios.post('/v1/calendar/events', payload)
          await trackUserProgress(PROGRESSION_ACTIONS.OPS_CALENDAR_EVENT_SAVED)
          if (payload?.type === 'client_visit' || payload?.type === 'prospecting') {
            await trackUserProgress(PROGRESSION_ACTIONS.CLIENT_EVENT_PROSPECTION_CREATED)
          }
          EventBus.$emit('snackbar', { message: this.$vuetify.lang.t('$vuetify.calendar_event_created') })
        }

        this.$emit('saved')
      } catch (error) {
        EventBus.$emit('snackbar', { axiosError: error })
      } finally {
        this.saving = false
      }
    },
    confirmDelete() {
      this.deleteDialog = true
    },
    async deleteEvent() {
      if (!this.event?.id) return

      this.deleting = true
      try {
        await axios.delete(`/v1/calendar/events/${this.event.id}`)
        EventBus.$emit('snackbar', { message: this.$vuetify.lang.t('$vuetify.calendar_event_deleted') })
        this.deleteDialog = false
        this.$emit('deleted')
      } catch (error) {
        EventBus.$emit('snackbar', { axiosError: error })
      } finally {
        this.deleting = false
      }
    },
    close() {
      this.showAddressMap = false
      this.$emit('input', false)
    },
  },
})
